/*
  scroll-sequence-hotspots.js
  Scroll-scrubbed image sequence with hotspots, for sections/scroll-sequence-hotspots.liquid
*/
(() => {
  if (customElements.get('scroll-sequence')) return;

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE_QUERY = '(max-width: 749px)';

  function parseTrack(str) {
    if (!str) return [];
    return String(str)
      .split(/[\n;|]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((part) => {
        let frame = null;
        let rest = part;
        const m = part.match(/^(\d+(?:\.\d+)?)\s*:\s*(.+)$/);
        if (m) { frame = parseFloat(m[1]); rest = m[2]; }
        const nums = rest.split(/[,\s]+/).filter(Boolean).map(Number);
        if (nums.length < 2 || nums.some((n) => Number.isNaN(n))) return null;
        return { f: frame, x: nums[0], y: nums[1] };
      })
      .filter(Boolean)
      .sort((a, b) => (a.f ?? 0) - (b.f ?? 0));
  }

  function sampleTrack(track, f) {
    if (!track.length) return null;
    if (track.length === 1 || track[0].f == null || f <= track[0].f) return track[0];
    const last = track[track.length - 1];
    if (f >= last.f) return last;
    for (let i = 1; i < track.length; i++) {
      const b = track[i];
      if (f <= b.f) {
        const a = track[i - 1];
        const t = (f - a.f) / ((b.f - a.f) || 1);
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      }
    }
    return last;
  }

  // Data Saver or a very slow connection: load every second frame only.
  function frameStep() {
    const c = navigator.connection;
    if (!c) return 1;
    if (c.saveData) return 2;
    return /(^|-)2g$/.test(c.effectiveType || '') ? 2 : 1;
  }

  // Load an image and decode it off the scroll path; resolves with the image or null.
  function loadDecoded(url) {
    return new Promise((resolve) => {
      const im = new Image();
      im.decoding = 'async';
      if ('fetchPriority' in im) im.fetchPriority = 'low';
      im.src = url;
      if (typeof im.decode === 'function') {
        im.decode().then(() => resolve(im), () => resolve(im.complete && im.naturalWidth ? im : null));
      } else {
        im.onload = () => resolve(im);
        im.onerror = () => resolve(null);
      }
    });
  }

  // First, last, then midpoints: scrubbing works early while the rest fills in.
  function loadOrder(n) {
    const order = [0, n - 1];
    const seen = new Set(order);
    const queue = [[0, n - 1]];
    while (queue.length) {
      const [a, b] = queue.shift();
      if (b - a <= 1) continue;
      const m = (a + b) >> 1;
      if (!seen.has(m)) { seen.add(m); order.push(m); }
      queue.push([a, m], [m, b]);
    }
    return order;
  }

  class ScrollSequence extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.canvas = this.querySelector('[data-sequence-canvas]');
      this.stage = this.querySelector('[data-sequence-stage]');
      if (!this.canvas || !this.stage) return;
      this._ready = true;

      const d = this.canvas.dataset;
      const ax = { left: 0, center: 0.5, right: 1 };
      const ay = { top: 0, center: 0.5, bottom: 1 };
      this.o = {
        desktop: d.desktopSrc || '',
        mobile: d.mobileSrc || '',
        type: d.filetype || 'webp',
        count: parseInt(d.frames, 10) || 0,
        digits: parseInt(d.digits, 10) || 4,
        start: parseInt(d.indexStart, 10) || 1,
        fit: d.fit === 'cover' ? 'cover' : 'contain',
        ax: ax[d.posX] ?? 0.5,
        ay: ay[d.posY] ?? 0.5,
        matchBg: d.matchBg === 'true',
        staticSrc: d.staticSrc || '',
        staticMobileSrc: d.staticMobileSrc || ''
      };
      this.staticMode = this.dataset.mode === 'static';
      if (this.staticMode) this.o.count = Math.max(1, this.o.count);
      this.o.last = this.o.start + this.o.count - 1;
      if (!this.o.count) return;

      this.ctx = this.canvas.getContext('2d');
      this.offset = parseFloat(this.dataset.offset) || 0;
      this.design = this.hasAttribute('data-design-mode');
      this.reduced = reduceMQ.matches || this.staticMode; // both show a single image
      this.mq = window.matchMedia(MOBILE_QUERY);
      this.fill = this.querySelector('[data-sequence-fill]');

      this.p = 0; this.idx = -1; this.exact = false;
      this.fw = 0; this.fh = 0; this.w = 0; this.h = 0; this.dpr = 1;
      this.box = { s: 1, dx: 0, dy: 0 };
      this.loadToken = 0;

      const all = [...this.querySelectorAll('[data-hotspot]')].map((btn) => {
        const dd = btn.dataset;
        const h = {
          btn,
          from: dd.from !== '' && dd.from != null ? parseFloat(dd.from) : this.o.start,
          to: dd.to !== '' && dd.to != null ? parseFloat(dd.to) : this.o.last,
          track: parseTrack(dd.track),
          mtrack: parseTrack(dd.trackMobile),
          content: this.querySelector(`[data-hotspot-content="${dd.hotspot}"]`),
          label: btn.getAttribute('aria-label') || '',
          on: false
        };
        h.onClick = () => this.openFrom(h);
        btn.addEventListener('click', h.onClick);
        return h;
      });
      this.missing = all.filter((h) => !h.track.length && !h.mtrack.length).length;
      this.hotspots = all.filter((h) => h.track.length || h.mtrack.length);

      this.setupPanel();
      if (this.reduced) this.classList.add('is-static');
      this.ratioMode = this.staticMode && this.dataset.staticHeight === 'image';
      if (this.ratioMode) this.classList.add('ssq--ratio');
      if (this.design) this.setupDesignHelper();

      this.onScroll = () => this.request();
      this.onResize = () => { this.useSource(); this.measure(); this.request(true); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.step = frameStep();
      this.near = !('IntersectionObserver' in window);
      if (!this.near) {
        this.io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this.near = true;
          this.io.disconnect();
          this.io = null;
          this.startQueue();
        }, { rootMargin: '200% 0px' });
        this.io.observe(this);
      }

      this.useSource();
      this.measure();
      this.request(true);
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.loadToken++;
      if (this.io) { this.io.disconnect(); this.io = null; }
      cancelAnimationFrame(this.raf);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.hotspots.forEach((h) => h.btn.removeEventListener('click', h.onClick));
      this.closePanel(true);
      if (this.panel) this.panel.remove();
    }

    /* ---------- frames ---------- */

    url(i) {
      return this.base + String(this.o.start + i).padStart(this.o.digits, '0') + '.' + this.o.type;
    }

    useSource() {
      const mobile = this.mq.matches && !!(this.staticMode ? this.o.staticMobileSrc : this.o.mobile);
      if (this.imgs && this.isMobileSrc === mobile) return;
      this.isMobileSrc = mobile;
      this.base = mobile ? this.o.mobile : this.o.desktop;
      this.imgs = new Array(this.o.count);
      this.loadToken++;
      this.fw = 0; this.fh = 0; this.idx = -1;
      this.active = 0;
      this.queue = [];
      if (this.reduced) { this.loadStatic(); return; }
      // The first frame loads right away so the section isn't empty; the rest wait until it's near.
      this.fetchFrame(0, this.loadToken);
      this.queued = false;
      if (this.near) this.startQueue();
    }

    startQueue() {
      if (this.reduced || this.queued || !this.imgs) return;
      this.queued = true;
      const last = this.o.count - 1;
      this.queue = loadOrder(this.o.count).filter((i) => i !== 0 && (i % this.step === 0 || i === last));
      this.pump(this.loadToken);
    }

    // Frames skipped on slow connections count as "drawn exactly" when their neighbour is shown.
    isSkipped(i) {
      return this.step > 1 && i % this.step !== 0 && i !== this.o.count - 1;
    }

    fetchFrame(i, token) {
      this.active++;
      loadDecoded(this.url(i)).then((im) => {
        if (token !== this.loadToken) return;
        this.active--;
        if (im) {
          this.imgs[i] = im;
          if (!this.fw) this.setFrameSize(im);
          if (!this.exact) this.request();
        }
        this.pump(token);
      });
    }

    pump(token) {
      while (this.active < 6 && this.queue.length) this.fetchFrame(this.queue.shift(), token);
    }

    loadStatic() {
      const token = this.loadToken;
      const im = new Image();
      im.onload = () => {
        if (token !== this.loadToken) return;
        this.imgs[0] = im;
        this.setFrameSize(im);
        this.request(true);
      };
      im.src = (this.staticMode && this.isMobileSrc && this.o.staticMobileSrc) || this.o.staticSrc || this.url(0);
    }

    setFrameSize(im) {
      this.fw = im.naturalWidth;
      this.fh = im.naturalHeight;
      if (this.ratioMode && this.fw && this.fh) this.stage.style.aspectRatio = `${this.fw} / ${this.fh}`;
      this.measure();
      this.sampleBg(im.src);
      this.request(true);
    }

    sampleBg(src) {
      if (!this.o.matchBg || this._bgDone) return;
      this._bgDone = true;
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = c.height = 1;
          const x = c.getContext('2d');
          // left edge, halfway down: avoids letterbox bands at the top and bottom of a frame
          x.drawImage(im, 2, Math.floor(im.naturalHeight / 2), 1, 1, 0, 0, 1, 1);
          const [r, g, b, a] = x.getImageData(0, 0, 1, 1).data;
          if (a < 250) return; // transparent image (e.g. a PNG cut-out): keep the section background
          const col = `rgb(${r}, ${g}, ${b})`;
          this.style.setProperty('--ssq-bg', col);
          if (this.panel) this.panel.style.setProperty('--ssq-media-bg', col);
        } catch (e) { /* host without CORS: keep the section background */ }
      };
      im.src = src;
    }

    nearest(i) {
      if (this.imgs[i]) return i;
      for (let d = 1; d < this.o.count; d++) {
        if (this.imgs[i - d]) return i - d;
        if (this.imgs[i + d]) return i + d;
      }
      return -1;
    }

    measure() {
      const r = this.stage.getBoundingClientRect();
      this.w = r.width; this.h = r.height;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(this.w * this.dpr);
      this.canvas.height = Math.round(this.h * this.dpr);
      if (this.fw) {
        const s = this.o.fit === 'cover'
          ? Math.max(this.w / this.fw, this.h / this.fh)
          : Math.min(this.w / this.fw, this.h / this.fh);
        this.box = { s, dx: (this.w - this.fw * s) * this.o.ax, dy: (this.h - this.fh * s) * this.o.ay };
      }
      // Label widths decide when a label flips to the dot's left near the right edge
      for (const h of this.hotspots) {
        const l = h.btn.querySelector('.ssq-hotspot__label');
        const inner = l && l.firstElementChild;
        h.lw = l ? Math.max(l.offsetWidth, inner ? inner.scrollWidth : 0) : 0;
      }
      this.idx = -1;
    }

    /* ---------- scroll loop ---------- */

    request(force) {
      if (force) this.force = true;
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.update(); });
    }

    update() {
      const force = this.force;
      this.force = false;
      const r = this.getBoundingClientRect();
      if (!force && (r.bottom < -100 || r.top > window.innerHeight + 100)) return;
      if (!this.reduced) {
        this.p = clamp((this.offset - r.top) / Math.max(1, r.height - this.h), 0, 1);
      }
      const i = this.reduced ? 0 : Math.round(this.p * (this.o.count - 1));
      if (i !== this.idx || !this.exact || force) this.draw(i);
      this.layout();
      if (this.fill) this.fill.style.transform = `scaleX(${this.p})`;
      if (this.badge) this.updateBadge();
    }

    draw(i) {
      this.idx = i;
      const k = this.nearest(i);
      if (k < 0 || !this.fw) { this.exact = false; return; }
      this.exact = k === i || (this.isSkipped(i) && Math.abs(k - i) === 1);
      const b = this.box;
      const c = this.ctx;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, this.canvas.width, this.canvas.height);
      c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      c.drawImage(this.imgs[k], b.dx, b.dy, this.fw * b.s, this.fh * b.s);
    }

    frameFloat() {
      return this.reduced ? this.o.start : this.o.start + this.p * (this.o.count - 1);
    }

    trackFor(h) {
      return this.isMobileSrc && h.mtrack.length ? h.mtrack : h.track;
    }

    layout() {
      if (!this.fw) return;
      const ff = this.frameFloat();
      const fno = Math.round(ff);
      const b = this.box;
      for (const h of this.hotspots) {
        const pt = sampleTrack(this.trackFor(h), ff);
        let on = !!pt && (this.staticMode || (fno >= h.from && fno <= h.to));
        let x = 0, y = 0;
        if (pt) {
          x = b.dx + (pt.x / 100) * this.fw * b.s;
          y = b.dy + (pt.y / 100) * this.fh * b.s;
          if (x < 12 || y < 12 || x > this.w - 12 || y > this.h - 12) on = false;
        }
        h.btn.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        h.btn.classList.toggle('is-flip', x + (h.lw || 0) + 40 > this.w);
        if (on !== h.on) {
          h.on = on;
          h.btn.classList.toggle('is-on', on);
          h.btn.tabIndex = on ? 0 : -1;
          h.btn.setAttribute('aria-hidden', String(!on));
        }
      }
    }

    /* ---------- detail panel ---------- */

    setupPanel() {
      const p = this.querySelector('[data-ssq-panel]');
      if (!p) return;
      this.panel = p;
      document.body.appendChild(p); // escape any transformed ancestors in the theme
      const q = (s) => p.querySelector(s);
      this.pm = {
        card: q('.ssq-panel__card'),
        slot: q('[data-panel-media-slot]'),
        cv: q('[data-panel-canvas]'),
        slide: q('[data-panel-slide]'),
        text: q('[data-panel-text]'),
        nav: q('[data-panel-nav]'),
        dots: q('[data-panel-dots]'),
        prev: q('[data-panel-prev]'),
        next: q('[data-panel-next]'),
        close: q('.ssq-panel__close')
      };
      p.querySelectorAll('[data-panel-close]').forEach((b) => b.addEventListener('click', () => this.closePanel()));
      this.pm.prev.addEventListener('click', () => this.go(this.pi - 1));
      this.pm.next.addEventListener('click', () => this.go(this.pi + 1));
      this.onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.closePanel(); }
        else if (e.key === 'ArrowRight') this.go(this.pi + 1);
        else if (e.key === 'ArrowLeft') this.go(this.pi - 1);
        else if (e.key === 'Tab') {
          const f = [...this.pm.card.querySelectorAll('button:not([disabled]), a[href]')].filter((el) => el.offsetParent !== null);
          if (!f.length) return;
          const first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      this.onPanelResize = () => { if (this.panelOpen && !this.pm.cv.hidden) this.drawZoom(this.plist[this.pi]); };
      window.addEventListener('resize', this.onPanelResize);
    }

    openFrom(h) {
      if (!this.panel) return;
      let list = this.hotspots.filter((x) => x.on);
      if (!list.includes(h)) list = [h];
      list.sort((a, b) => a.from - b.from);
      this.plist = list;
      this.pi = list.indexOf(h);
      this.returnFocus = h.btn;
      clearTimeout(this.hideT);
      this.panelOpen = true;
      this.panel.hidden = false;
      this.prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.fillPanel();
      this.playSlide();
      requestAnimationFrame(() => requestAnimationFrame(() => this.panel.classList.add('is-open')));
      document.addEventListener('keydown', this.onKey);
      this.pm.close.focus({ preventScroll: true });
    }

    closePanel(immediate) {
      if (!this.panel || !this.panelOpen) return;
      this.panelOpen = false;
      this.hotspots.forEach((x) => x.btn.classList.remove('is-active'));
      this.panel.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      document.documentElement.style.overflow = this.prevOverflow || '';
      const hide = () => { this.panel.hidden = true; };
      if (immediate || reduceMQ.matches) hide();
      else this.hideT = setTimeout(hide, 600);
      if (!immediate && this.returnFocus) this.returnFocus.focus({ preventScroll: true });
      if (immediate) window.removeEventListener('resize', this.onPanelResize);
    }

    fillPanel() {
      const h = this.plist[this.pi];
      const src = h.content;
      const media = src ? src.querySelector('[data-media]') : null;
      const mediaHTML = media ? media.innerHTML.trim() : '';
      this.pm.text.innerHTML = src ? (src.querySelector('[data-text]')?.innerHTML || '') : '';
      this.pm.card.setAttribute('aria-label', h.label);
      if (mediaHTML) {
        this.pm.slot.innerHTML = mediaHTML;
        this.pm.slot.hidden = false;
        this.pm.cv.hidden = true;
      } else {
        this.pm.slot.innerHTML = '';
        this.pm.slot.hidden = true;
        this.pm.cv.hidden = false;
        this.drawZoom(h);
      }
      this.pm.dots.innerHTML = '';
      this.plist.forEach((x, j) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ssq-panel__dot';
        b.setAttribute('aria-label', x.label);
        if (j === this.pi) b.setAttribute('aria-current', 'true');
        b.addEventListener('click', () => this.go(j));
        this.pm.dots.appendChild(b);
      });
      this.pm.nav.hidden = this.plist.length < 2;
      this.hotspots.forEach((x) => x.btn.classList.toggle('is-active', x === h));
    }

    playSlide() {
      const s = this.pm.slide;
      s.classList.remove('is-entering');
      void s.offsetWidth; // restart the fade-up
      s.classList.add('is-entering');
    }

    // No image on the metaobject: show a close-up of the frame around the hotspot.
    drawZoom(h) {
      const cv = this.pm.cv;
      const r = cv.getBoundingClientRect();
      if (!r.width || !this.fw) return;
      const d = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.round(r.width * d);
      cv.height = Math.round(r.height * d);
      const mid = this.reduced ? this.o.start : clamp((h.from + h.to) / 2, this.o.start, this.o.last);
      const k = this.nearest(this.reduced ? 0 : Math.round(mid - this.o.start));
      const pt = sampleTrack(this.trackFor(h), mid);
      const c = cv.getContext('2d');
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, cv.width, cv.height);
      if (k < 0 || !pt) return;
      const z = r.width / (this.fw * 0.4);
      const px = (pt.x / 100) * this.fw;
      const py = (pt.y / 100) * this.fh;
      c.setTransform(d * z, 0, 0, d * z, d * (r.width / 2 - px * z), d * (r.height / 2 - py * z));
      c.drawImage(this.imgs[k], 0, 0, this.fw, this.fh);
      c.setTransform(d, 0, 0, d, 0, 0);
      c.lineWidth = 2;
      const ps = getComputedStyle(this.panel);
      c.strokeStyle = ps.getPropertyValue('--ssq-ring').trim() || ps.getPropertyValue('--ssq-panel-accent').trim() || '#260303';
      c.beginPath();
      c.arc(r.width / 2, r.height / 2, 26, 0, Math.PI * 2);
      c.stroke();
    }

    go(j) {
      if (!this.plist || this.plist.length < 2) return;
      j = (j + this.plist.length) % this.plist.length;
      if (j === this.pi) return;
      this.pi = j;
      this.fillPanel();
      this.playSlide();
    }

    /* ---------- theme editor helper (never shown to customers) ---------- */

    setupDesignHelper() {
      const b = document.createElement('div');
      b.className = 'ssq-design';
      b.innerHTML =
        '<span data-f></span>' +
        '<input type="text" readonly aria-label="Hotspot position" placeholder="Click the image to copy a position">' +
        (this.missing ? `<span class="ssq-design__warn">${this.missing} hotspot(s) have no positions yet</span>` : '');
      this.stage.appendChild(b);
      const pin = document.createElement('span');
      pin.className = 'ssq-design__pin';
      this.stage.appendChild(pin);
      this.badge = b;
      this.badgeF = b.querySelector('[data-f]');
      this.badgeIn = b.querySelector('input');
      this.stage.addEventListener('click', (e) => {
        if (e.target.closest('.ssq-hotspot, .ssq-design') || !this.fw) return;
        const r = this.stage.getBoundingClientRect();
        const sx = e.clientX - r.left;
        const sy = e.clientY - r.top;
        const x = ((sx - this.box.dx) / (this.fw * this.box.s)) * 100;
        const y = ((sy - this.box.dy) / (this.fh * this.box.s)) * 100;
        if (x < 0 || x > 100 || y < 0 || y > 100) return;
        const v = this.staticMode
          ? `${x.toFixed(1)}, ${y.toFixed(1)}`
          : `${Math.round(this.frameFloat())}: ${x.toFixed(1)}, ${y.toFixed(1)}`;
        this.badgeIn.value = v;
        this.badgeIn.select();
        if (navigator.clipboard) navigator.clipboard.writeText(v).catch(() => {});
        pin.style.transform = `translate3d(${sx}px, ${sy}px, 0)`;
        pin.classList.add('is-on');
      });
    }

    updateBadge() {
      const which = this.isMobileSrc ? (this.staticMode ? ' (mobile image)' : ' (mobile frames)') : '';
      this.badgeF.textContent = this.staticMode
        ? 'Static image' + which
        : `Frame ${Math.round(this.frameFloat())} of ${this.o.last}` + which;
    }
  }

  customElements.define('scroll-sequence', ScrollSequence);
})();